import { useCallback, useEffect, useState } from 'react';
import {
  procurementPlanningApi,
  type ProcurementDashboard,
  type ProcurementPlan,
  type ProcurementRecommendation,
  type ProcurementRisk,
  type ProcurementScenario,
  type PurchaseRequirement,
  type SupplierPlan,
} from '../../api/procurementplanning';

type Tab = 'dashboard' | 'requirements' | 'suppliers' | 'risks' | 'recommendations' | 'scenarios' | 'budget';
type Theme = 'light' | 'dark';

function fmtMoney(n: number) {
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(1)}K`;
  return `$${n.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}

export function ProcurementPlanningPage() {
  const [theme, setTheme] = useState<Theme>('light');
  const dark = theme === 'dark';
  const [tab, setTab] = useState<Tab>('dashboard');
  const [dashboard, setDashboard] = useState<ProcurementDashboard | null>(null);
  const [plan, setPlan] = useState<ProcurementPlan | null>(null);
  const [requirements, setRequirements] = useState<PurchaseRequirement[]>([]);
  const [suppliers, setSuppliers] = useState<SupplierPlan[]>([]);
  const [risks, setRisks] = useState<ProcurementRisk[]>([]);
  const [recommendations, setRecommendations] = useState<ProcurementRecommendation[]>([]);
  const [scenarios, setScenarios] = useState<ProcurementScenario[]>([]);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const [dash, plans] = await Promise.all([procurementPlanningApi.dashboard(), procurementPlanningApi.listPlans()]);
      setDashboard(dash);
      const p = plans[0] ?? null;
      setPlan(p);
      if (!p) return;
      const [reqs, sups, rsk, recs, sc] = await Promise.all([
        procurementPlanningApi.listRequirements(p.id),
        procurementPlanningApi.listSupplierPlans(p.id),
        procurementPlanningApi.listRisks(p.id),
        procurementPlanningApi.listRecommendations(p.id),
        procurementPlanningApi.listScenarios(p.id),
      ]);
      setRequirements(reqs);
      setSuppliers(sups);
      setRisks(rsk);
      setRecommendations(recs);
      setScenarios(sc);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load procurement planning');
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const shell = dark ? 'min-h-screen bg-slate-950 text-slate-100' : 'min-h-screen bg-slate-50 text-slate-900';
  const card = dark ? 'rounded-xl border border-slate-800 bg-slate-900 p-5' : 'rounded-xl border border-slate-200 bg-white p-5 shadow-sm';
  const header = dark ? 'border-b border-slate-800 bg-slate-900' : 'border-b border-slate-200 bg-white';
  const muted = dark ? 'text-slate-400' : 'text-slate-600';

  const tabs: { id: Tab; label: string }[] = [
    { id: 'dashboard', label: 'Dashboard' },
    { id: 'requirements', label: 'Purchase Requirements' },
    { id: 'suppliers', label: 'Supplier Planning' },
    { id: 'risks', label: 'Risk Dashboard' },
    { id: 'recommendations', label: 'Recommendations' },
    { id: 'scenarios', label: 'Scenarios' },
    { id: 'budget', label: 'Budget' },
  ];

  return (
    <div className={shell}>
      <div className={`${header} px-6 py-4`}>
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold">Enterprise Procurement Planning</h1>
            <p className={`mt-1 text-sm ${muted}`}>Purchase requirement planning, supplier intelligence, EOQ optimization, and AI procurement recommendations</p>
          </div>
          <div className="flex gap-2">
            <button type="button" onClick={() => setTheme(dark ? 'light' : 'dark')} className={`rounded-lg px-3 py-2 text-sm ${dark ? 'bg-slate-800' : 'bg-slate-100'}`} aria-label="Toggle dark mode">{dark ? 'Light' : 'Dark'}</button>
            <button type="button" onClick={refresh} className="rounded-lg bg-orange-600 px-4 py-2 text-sm font-medium text-white hover:bg-orange-700">Refresh</button>
          </div>
        </div>
        {plan && <p className={`mt-2 text-sm ${muted}`}>{plan.name} · Budget {fmtMoney(plan.budget_amount)} · Spend {fmtMoney(plan.planned_spend)} · Health {plan.health_score.toFixed(0)}</p>}
      </div>
      {error && <div className="mx-6 mt-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900 dark:bg-red-950 dark:text-red-300" role="alert">{error}</div>}
      <div className={`${header} px-6`}>
        <nav className="flex gap-1 overflow-x-auto" role="tablist">
          {tabs.map((t) => (
            <button key={t.id} type="button" role="tab" aria-selected={tab === t.id} onClick={() => setTab(t.id)}
              className={`whitespace-nowrap border-b-2 px-4 py-3 text-sm font-medium ${tab === t.id ? 'border-orange-600 text-orange-600' : 'border-transparent text-slate-500'}`}>{t.label}</button>
          ))}
        </nav>
      </div>
      <div className="p-6">
        {tab === 'dashboard' && dashboard && (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {[
              { label: 'Active Plans', value: dashboard.active_plans },
              { label: 'Planned Spend', value: fmtMoney(dashboard.planned_spend) },
              { label: 'Procurement Savings', value: fmtMoney(dashboard.procurement_savings) },
              { label: 'Health Score', value: dashboard.health_score.toFixed(0) },
              { label: 'Avg Supplier Score', value: dashboard.avg_supplier_score.toFixed(0) },
              { label: 'Avg Lead Time', value: `${dashboard.avg_lead_time_days.toFixed(0)} days` },
              { label: 'Container Utilization', value: `${dashboard.container_utilization.toFixed(0)}%` },
              { label: 'Open Risks', value: dashboard.open_risks },
            ].map((m) => (
              <div key={m.label} className={card}><p className={`text-sm ${muted}`}>{m.label}</p><p className="mt-1 text-2xl font-semibold">{m.value}</p></div>
            ))}
          </div>
        )}
        {tab === 'requirements' && requirements.map((r) => (
          <div key={r.id} className={`${card} mb-4`}>
            <h3 className="font-medium">Order {r.planned_order_qty.toLocaleString()} units · EOQ {r.eoq.toLocaleString()}</h3>
            <p className={`mt-1 text-sm ${muted}`}>{r.explanation}</p>
            <p className="mt-2 text-sm">Net req {r.net_requirement.toLocaleString()} · Cost {fmtMoney(r.total_cost)} · Container {r.container_fill_pct}%</p>
          </div>
        ))}
        {tab === 'suppliers' && (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {suppliers.map((s) => (
              <div key={s.id} className={card}>
                <h3 className="font-medium">{s.supplier_name}</h3>
                <p className={`mt-1 text-sm ${muted}`}>Score {s.supplier_score} · Lead {s.lead_time_days}d · Util {s.utilization_pct}%</p>
                {s.preferred_supplier && <span className="mt-2 inline-block rounded bg-orange-100 px-2 py-0.5 text-xs text-orange-700 dark:bg-orange-900 dark:text-orange-300">Preferred</span>}
              </div>
            ))}
          </div>
        )}
        {tab === 'risks' && risks.map((r) => (
          <div key={r.id} className={`${card} mb-4`}>
            <span className="rounded bg-red-100 px-2 py-0.5 text-xs text-red-700 dark:bg-red-900 dark:text-red-300">{r.risk_type.replace(/_/g, ' ')}</span>
            <p className="mt-2 text-sm"><strong>Impact:</strong> {r.business_impact}</p>
            <p className="mt-1 text-sm"><strong>Mitigation:</strong> {r.recommended_mitigation}</p>
          </div>
        ))}
        {tab === 'recommendations' && recommendations.map((rec) => (
          <div key={rec.id} className={`${card} mb-4`}>
            <h3 className="font-medium">{rec.title}</h3>
            <p className={`mt-1 text-sm ${muted}`}>{rec.business_explanation}</p>
            <p className="mt-2 text-sm">Savings {fmtMoney(rec.expected_savings)} · Confidence {(rec.confidence * 100).toFixed(0)}%</p>
          </div>
        ))}
        {tab === 'scenarios' && (
          <div className="grid gap-4 md:grid-cols-3">
            {scenarios.map((sc) => (
              <div key={sc.id} className={card}>
                <h3 className="font-medium">{sc.name}</h3>
                <p className="mt-2 text-xl font-semibold">{fmtMoney(sc.total_cost)}</p>
                <p className={`text-sm ${muted}`}>Lead {sc.lead_time_days}d · Risk {sc.risk_score} · Util {sc.supplier_utilization}%</p>
              </div>
            ))}
          </div>
        )}
        {tab === 'budget' && plan && dashboard && (
          <div className={`${card} max-w-xl`}>
            <h2 className="text-lg font-medium">Budget Dashboard</h2>
            <div className="mt-4 space-y-2 text-sm">
              <p>Budget: <strong>{fmtMoney(plan.budget_amount)}</strong></p>
              <p>Planned Spend: <strong>{fmtMoney(plan.planned_spend)}</strong></p>
              <p>Variance: <strong className={dashboard.budget_variance > 0 ? 'text-red-600' : 'text-emerald-600'}>{dashboard.budget_variance > 0 ? '+' : ''}{dashboard.budget_variance.toFixed(1)}%</strong></p>
              <p>Savings Opportunity: <strong>{fmtMoney(plan.procurement_savings)}</strong></p>
              <p>Supplier Dependency Index: <strong>{plan.supplier_dependency_index.toFixed(0)}</strong></p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
